/**
 * RISKS
 * Represents the types and interfaces used in the risks feature.
 */

import { v4 as uuid } from "uuid"
import { Unit } from "../../unit/model/unit.types"
import { faArchive, faCheck, IconDefinition } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"

export const RISK_ENTITY = "risk"

export type RiskAddMode = "assistant" | "model" | "blank" | "later" | null

export type RiskType = "psy" | "phy" //Type of risk (physical or psy)

/** List of tasks done / to do for a risk */
export interface RiskAction {
  id: string
  name: string
  description: string
  dateEnd: Date
  dateEndPlanned: Date
  dateStarted: Date
  budget: number
  budgetSpend: number
  UserResponsibleId: string
}

/** Group of risks by name attribute */
export interface RiskFamily {
  id: string
  Risks: Risk[]
  riskMax: Risk
  name: string
  size: number
}

export interface RiskIncident {
  id: string
  date: Date
  description: string
}

export type RiskStatusId = "validated" | "archived"

export interface RiskStatus {
  id: RiskStatusId
  label: string
  labelEmpty?: string
  color: string
  icon: IconDefinition
}

export const RISK_STATUS_MAP: Record<RiskStatusId, RiskStatus> = {
  validated: {
    id: "validated",
    label: t("Validé"),
    color: "#61bd71",
    icon: faCheck
  },
  archived: {
    id: "archived",
    label: t("Archivé"),
    color: "lightgrey",
    icon: faArchive
  }
}

interface RiskOptions {
  askSave?: boolean //Display save button on creation (exemple, i create a risk from AI assistant i just want to confirm)
  scales?: Partial<RiskScales>
}

/**
 * Business risk structure
 */
export interface Risk {
  alertColor: string | null //Color for alert (yellow if soon, red if expired)
  alertExpired: boolean //Risk that need to be updated (more than one year)
  actions: RiskAction[]
  associatedRisks: Risk[] //If other risks share the same values
  attributes: Record<string, string>
  commentary: string
  dateArchived: Date | null
  dateArchivedLabel: string
  dateValidated: Date | null
  dateValidatedLabel: string
  dateValidatedToday: boolean
  // editedRisk: Risk | null //User that edit the validated risk
  EstablishmentId: string
  name: string //First attribute
  nameSecondary: string //Second attribute
  frequency: number
  frequencyLabel: string
  frequencyScale: RiskScaleItem[]
  frequencyScaleMax: number | null
  id: string
  isEditable: boolean //Not available to edition (ex as contributor and not in my unit)
  mediaIds: string[]
  options: RiskOptions
  prevention: number
  preventionLabel: string
  preventionScale: RiskScaleItem[]
  preventionScaleMax: number | null
  residualScale: RiskResidualItem[]
  severity: number
  severityLabel: string
  severityScale: RiskScaleItem[]
  severityScaleMax: number | null
  sourceId?: string | null // Root ID for version tracking
  rps: boolean // RPS flag
  UnitId: string | null
  unitName: string
  unitSize: number | null
  incidents: RiskIncident[]
  valueEffective: number | null //Value of risk calculated (after prevention applied)
  valueEffectiveColor: string | null //Color (green to red)
  valueEffectiveLabel: string | null //Text to display for risk
  valueEffectiveIndex: number | null //Which range for value effective (1 = weak, 2 = average...)
  valueInitial: number | null //Value of risk calculated (without prevention applied)
  valueInitialColor: string | null //Color (green to red)
  valueInitialLabel: string | null //Text to display for risk
}

//List of columns for the mapping
export const RISK_MAPPING_COLUMNS = [
  { id: "frequency", text: "risk_item_f", description: "risk_item_f_desc_1" },
  { id: "prevention", text: "risk_item_p", description: "risk_item_p_desc_1" },
  { id: "severity", text: "risk_item_g", description: "risk_item_g_desc_1" },
  { id: "rps", text: "rps", description: "rps_help" },
  { id: "dateValidated", text: "date_validated", description: "date_validated_help" },
  { id: "unitName", text: "unit", description: "unit_name_help" },
  { id: "unitDescription", text: "unit_description", description: "unit_description_help" },
  { id: "unitSize", text: "unit_size", description: "unit_size_help" }
] as const

// dérive le type automatiquement à partir du tableau
export type RiskMappingColumns = (typeof RISK_MAPPING_COLUMNS)[number]

//Prepare mapping of file
export interface RiskMapping {
  importColumns: { sample: string[]; name: string; mapping: string | null; isCustom?: boolean }[]
  rows: string[][]
  risks: Risk[]
  units: Unit[]
}

export type RiskLevelId = "green" | "yellow" | "orange" | "red"

/**
 * Crée un risque vide, avec possibilité de surcharger les valeurs par défaut.
 */
export const createEmptyRisk = (overrides?: Partial<Risk>): Risk => {
  const defaultRisk: Risk = {
    alertColor: null,
    alertExpired: false,
    associatedRisks: [],
    id: uuid(),
    // editedRisk: null,
    sourceId: null,
    rps: false,
    commentary: "",
    dateArchived: null,
    dateArchivedLabel: "",
    dateValidated: null,
    dateValidatedLabel: "",
    dateValidatedToday: false,
    name: "",
    nameSecondary: "",
    frequency: 2,
    frequencyLabel: "",
    frequencyScale: DEFAULT_RISK_SCALES.frequencyScale,
    frequencyScaleMax: null,
    isEditable: true,
    severity: 1,
    severityLabel: "",
    severityScale: DEFAULT_RISK_SCALES.severityScale,
    severityScaleMax: null,
    options: { scales: {} },
    prevention: 1,
    preventionLabel: "",
    preventionScale: DEFAULT_RISK_SCALES.preventionScale,
    preventionScaleMax: null,
    attributes: {},
    unitName: "",
    unitSize: null,
    actions: [],
    mediaIds: [],
    residualScale: DEFAULT_RISK_SCALES.residualRiskScale,
    // status: RISK_STATUS_MAP.validated,
    UnitId: null,
    EstablishmentId: "",
    incidents: [],
    valueEffective: null,
    valueEffectiveColor: null,
    valueEffectiveLabel: null,
    valueEffectiveIndex: null,
    valueInitial: null,
    valueInitialColor: null,
    valueInitialLabel: null
  }

  return {
    ...defaultRisk,
    ...overrides
  }
}

// === Risk scales ===
export interface RiskScaleItem {
  label: string
  value: number
  color?: string
}

export interface RiskScaleMeta {
  min: number
  max: number
  step?: number
}

export interface RiskResidualItem {
  min: number
  max: number
  label: string
  color?: string
}

export interface RiskScales {
  severityScale: RiskScaleItem[]
  frequencyScale: RiskScaleItem[]
  preventionScale: RiskScaleItem[]
  residualRiskScale: RiskResidualItem[]
}

export const RISK_SCALE_META: Record<keyof RiskScales, RiskScaleMeta> = {
  severityScale: { min: 1, max: 20, step: 1 },
  frequencyScale: { min: 1, max: 20, step: 1 },
  preventionScale: { min: 1, max: 20, step: 1 },
  residualRiskScale: { min: 1, max: 256, step: 1 }
}

/**
 * Calcule la borne max de l'échelle résiduelle = fréquence max × gravité max
 */
export function getResidualMax(): number {
  return RISK_SCALE_META.severityScale.max * RISK_SCALE_META.frequencyScale.max
}

/**
 * Échelles par défaut
 */
export const DEFAULT_RISK_SCALES: RiskScales = {
  severityScale: [
    { label: "Gêne : irritations, migraines, inconfort temporaire, fatigue…", value: 1 },
    { label: "Atteinte légère : ITT inferieure à 8 jours, pas de séquelles", value: 4 },
    { label: "Atteinte marquée : ITT supérieure à 8 jours et inférieure à 3 mois, atteinte fonctionnelle", value: 7 },
    { label: "Atteinte permanente : invalidité, incapacité permanente…", value: 10 }
  ],
  frequencyScale: [
    {
      label: "Arrive quelques fois dans l’année : Le travailleur est exposé moins d’une fois par semaine au danger",
      value: 1
    },
    { label: "Une fois par semaine : Le travailleur est exposé au moins une fois par semaine au danger", value: 3 },
    { label: "Tous les jours : Le travailleur est exposé une ou plusieurs fois par jour au danger", value: 5 },
    { label: "Permanent : Le travailleur est exposé  en permanence au danger", value: 10 }
  ],
  preventionScale: [
    {
      label:
        "Nulle : Le danger subsiste: des mesures ont été prises pour avertir ou mettre en garde la personne (affichage, interdictions…)",
      value: 1
    },
    {
      label:
        "Insatisfaisante : Protection individuelle: des mesures ont été prises pour protéger la personne (EPI, procédures, formation…)",
      value: 3
    },
    {
      label:
        "Bonne : Protection collective: des mesures ont été prises pour isoler le danger (écrans de protection, dispositifs de capotage, 5S, formation…)",
      value: 7
    },
    {
      label:
        "Maximale : Protection collective + l’ensemble des moyens de maîtrise fait l’objet d’un suivi et d’une actualisation régulière",
      value: 10
    }
  ],
  residualRiskScale: [
    { min: 1, label: "Faible", max: 3, color: "green" },
    { min: 4, label: "Modéré", max: 15, color: "yellow" },
    { min: 16, label: "Moyen", max: 39, color: "orange" },
    { min: 40, label: "Élevé", max: getResidualMax(), color: "red" }
  ]
}

/**
 * RPS (Risk Assessment System) scales par défaut
 * Same structure as PHY but with domain-specific labels
 */
export const DEFAULT_RPS_SCALES: RiskScales = {
  severityScale: [
    { label: "Faible : Gêne mineure, légères répercussions", value: 1 },
    { label: "Modéré : Impact notable sur la santé", value: 4 },
    { label: "Important : Impact significatif, arrêt de travail possible", value: 7 },
    { label: "Critique : Impact majeur, incapacité durable", value: 10 }
  ],
  frequencyScale: [
    { label: "Occasionnel : Quelques fois par an", value: 1 },
    { label: "Régulier : Hebdomadaire", value: 3 },
    { label: "Fréquent : Quotidien", value: 5 },
    { label: "Continu : Permanent", value: 10 }
  ],
  preventionScale: [
    { label: "Nulle : Aucune mesure de prévention", value: 1 },
    { label: "Minimale : Sensibilisation et formation", value: 3 },
    { label: "Appropriée : Mesures d’amélioration mises en place", value: 7 },
    { label: "Complète : Plan d’action suivi régulièrement", value: 10 }
  ],
  residualRiskScale: [
    { min: 1, label: "Faible", max: 3, color: "green" },
    { min: 4, label: "Modéré", max: 15, color: "yellow" },
    { min: 16, label: "Moyen", max: 39, color: "orange" },
    { min: 40, label: "Élevé", max: getResidualMax(), color: "red" }
  ]
}
